import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleQuestionMark } from 'lucide-react';

import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { MyReportsList } from './my-reports-list';
import { ReportBugForm } from './report-bug-form';

const TABS = { new: 'new', mine: 'mine' } as const;

type ReportBugTab = (typeof TABS)[keyof typeof TABS];

interface IReportBugDialogProps {
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    /**
     * Report to land on, set when the dialog was opened by clicking a
     * notification rather than the header button. Opens on "My reports" with
     * that thread already expanded.
     */
    readonly openReportId?: string | undefined;
}

/**
 * Deliberately not a route — the launcher is the header "?" button, reachable
 * from every page, and sending the user away from the page they were on would
 * lose the context the report is about.
 *
 * Two tabs: filing something new, and the reporter's own history with the
 * conversation each report turned into. Submitting switches to "My reports"
 * rather than closing, so the reply thread the user was just promised is the
 * next thing they see.
 */
export function ReportBugDialog({ open, onOpenChange, openReportId }: IReportBugDialogProps) {
    const { t } = useTranslation();
    const [tab, setTab] = useState<ReportBugTab>(openReportId ? TABS.mine : TABS.new);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-140" showCloseButton>
                <DialogHeader className="px-5 pt-5 pb-1">
                    {/* Same icon as the header "?" button that opens this, so the
                        dialog reads as that button expanding rather than as an
                        unrelated panel appearing. */}
                    <div className="flex items-start gap-3 pr-8">
                        <span
                            className="bg-bg-icon-tile flex size-9 shrink-0 items-center justify-center rounded-lg"
                            aria-hidden="true"
                        >
                            <CircleQuestionMark className="text-primary-600 size-4.5" />
                        </span>
                        <div className="flex min-w-0 flex-col gap-0.5">
                            <DialogTitle className="text-base">
                                {t('bugReports.dialog.title', 'Report an issue')}
                            </DialogTitle>
                            <DialogDescription className="text-xs">
                                {t(
                                    'bugReports.dialog.description',
                                    'Tell us what happened and attach a screenshot as proof.',
                                )}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <Tabs
                    value={tab}
                    onValueChange={(next) => setTab(next as ReportBugTab)}
                    className="min-h-0 flex-1 flex-col gap-0"
                >
                    <TabsList variant="line" className="px-5">
                        <TabsTrigger value={TABS.new} className="flex-none">
                            {t('bugReports.dialog.newTab', 'New Report')}
                        </TabsTrigger>
                        <TabsTrigger value={TABS.mine} className="flex-none">
                            {t('bugReports.dialog.myReportsTab', 'My Reports')}
                        </TabsTrigger>
                    </TabsList>

                    {/* `forceMount` keeps the form alive while the other tab is
                        showing. Radix unmounts an inactive panel by default,
                        which would silently discard a half-written report the
                        moment someone glanced at their history. It also stops
                        hiding the panel for us, hence the explicit `hidden`. */}
                    <TabsContent
                        value={TABS.new}
                        forceMount
                        className={cn('flex min-h-0 flex-col', tab !== TABS.new && 'hidden')}
                    >
                        <ReportBugForm
                            onSubmitted={() => setTab(TABS.mine)}
                            onCancel={() => onOpenChange(false)}
                        />
                    </TabsContent>

                    {/* Not force-mounted: the list is server state, so React
                        Query holds it and the tab can re-render from cache. */}
                    <TabsContent value={TABS.mine} className="flex min-h-0 flex-col">
                        <DialogBody className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
                            <MyReportsList
                                onFileNew={() => setTab(TABS.new)}
                                openReportId={openReportId}
                            />
                        </DialogBody>
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
}
